import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Megaphone, LayoutGrid, List, Search, TrendingUp, Euro, Pencil, BarChart3 } from 'lucide-react'
import { advApi } from '@/services/api'
import { Badge } from '@/components/common/Badge'
import { StatCard } from '@/components/common/StatCard'
import { CampaignCard } from '@/components/adv/CampaignCard'
import { CampaignDetailModal } from '@/components/adv/CampaignDetailModal'
import { CampaignFormModal } from '@/components/adv/CampaignFormModal'
import { useProjectFilter, ProjectFilterBanner } from '@/components/common/ProjectFilterBanner'
import { cn } from '@/lib/utils'
import type { AdCampaign, CampaignStatus, CampaignType, Platform } from '@/types'

// ---------------------------------------------------------------
// Constants
// ---------------------------------------------------------------

const STATUS_VARIANT: Record<CampaignStatus, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  ACTIVE: 'success', PAUSED: 'warning', DRAFT: 'neutral',
  PENDING_REVIEW: 'info', COMPLETED: 'info', ARCHIVED: 'neutral',
}

const STATUS_LABEL: Record<CampaignStatus, string> = {
  ACTIVE: 'Attiva', PAUSED: 'In pausa', DRAFT: 'Bozza',
  PENDING_REVIEW: 'In revisione', COMPLETED: 'Completata', ARCHIVED: 'Archiviata',
}

const PLATFORM_LABEL: Partial<Record<Platform, string>> = {
  INSTAGRAM: 'Instagram', FACEBOOK: 'Facebook', LINKEDIN: 'LinkedIn',
  TWITTER: 'Twitter', TIKTOK: 'TikTok', YOUTUBE: 'YouTube', PINTEREST: 'Pinterest',
}

// ---------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------
function CampaignSkeleton() {
  return (
    <div className="h-52 bg-slate-100 rounded-2xl animate-pulse" />
  )
}

// ---------------------------------------------------------------
// Backend response shape
// The controller wraps listCampaigns result as:
// { success: true, data: { items, total, page, limit, totalPages } }
// advApi.listCampaigns uses apiClient.get which returns response.data directly.
// So the resolved value is: { success: boolean, data: { items: AdCampaign[], total: number, ... } }
// ---------------------------------------------------------------

interface CampaignsApiResponse {
  success: boolean
  data: {
    items: AdCampaign[]
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

// ---------------------------------------------------------------
// Main page
// ---------------------------------------------------------------
export function ADVPage() {
  const pf = useProjectFilter()

  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<CampaignStatus | ''>('')
  const [filterPlatform, setFilterPlatform] = useState<Platform | ''>('')
  const [filterType, setFilterType] = useState<CampaignType | ''>('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [selectedCampaign, setSelectedCampaign] = useState<AdCampaign | null>(null)
  const [editingCampaign, setEditingCampaign] = useState<AdCampaign | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)

  // Fetch campaigns from real API
  const { data: apiResponse, isLoading, error } = useQuery<CampaignsApiResponse>({
    queryKey: ['campaigns'],
    queryFn: () => advApi.listCampaigns({ limit: 100 }) as Promise<CampaignsApiResponse>,
    staleTime: 30_000,
  })

  const campaigns: AdCampaign[] = apiResponse?.data?.items ?? []
  const total = apiResponse?.data?.total ?? 0

  // ---------------------------------------------------------------
  // Client-side filtering (project filter + search + status + platform + type)
  // ---------------------------------------------------------------
  const filtered = campaigns.filter((c) => {
    if (pf.isFiltered && pf.clientId && c.clientId !== pf.clientId) return false
    if (search) {
      const q = search.toLowerCase()
      const nameMatch = c.name.toLowerCase().includes(q)
      const clientMatch = c.client?.name?.toLowerCase().includes(q) ?? false
      if (!nameMatch && !clientMatch) return false
    }
    if (filterStatus && c.status !== filterStatus) return false
    if (filterPlatform && !c.platforms.includes(filterPlatform)) return false
    if (filterType && c.type !== filterType) return false
    return true
  })

  // ---------------------------------------------------------------
  // Statuses and platforms present in real data (for dynamic filter chips)
  // ---------------------------------------------------------------
  const presentStatuses = Array.from(new Set(campaigns.map((c) => c.status as CampaignStatus)))
  const presentPlatforms = Array.from(new Set(campaigns.flatMap((c) => c.platforms as Platform[])))

  // ---------------------------------------------------------------
  // KPI summary — computed from real data
  // ---------------------------------------------------------------
  const activeCampaigns = campaigns.filter((c) => c.status === 'ACTIVE').length
  const totalBudget = campaigns.reduce((acc, c) => acc + c.totalBudget, 0)
  const totalSpent = campaigns.reduce((acc, c) => acc + c.spentBudget, 0)

  // Aggregate CTR from metrics if available on campaigns
  const metricsWithCtr = campaigns.flatMap((c) => c.metrics ?? []).filter((m) => m.ctr > 0)
  const avgCtr = metricsWithCtr.length > 0
    ? metricsWithCtr.reduce((sum, m) => sum + m.ctr, 0) / metricsWithCtr.length
    : 0

  const fmt = (n: number, opts?: Intl.NumberFormatOptions) =>
    new Intl.NumberFormat('it-IT', opts).format(n)

  // ---------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------
  function handleEditCampaign(campaign: AdCampaign) {
    setSelectedCampaign(null)
    setEditingCampaign(campaign)
  }

  function handleFormClose() {
    setEditingCampaign(null)
    setShowCreateForm(false)
  }

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  return (
    <div className="space-y-6">

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Campagne ADV</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Gestione campagne pubblicitarie
            {!isLoading && (
              <> — {activeCampaigns} attive su {total} totali</>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nuova Campagna
        </button>
      </div>

      {/* Project filter banner */}
      <ProjectFilterBanner
        projectId={pf.projectId}
        projectName={pf.projectName}
        clientName={pf.clientName}
        onClear={pf.clearFilter}
      />

      {/* KPI Summary */}
      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Campagne Totali"
            value={total}
            hint={`${activeCampaigns} attive`}
            icon={Megaphone}
            tone={activeCampaigns > 0 ? 'primary' : 'neutral'}
          />
          <StatCard
            label="Budget Totale"
            value={fmt(totalBudget, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
            hint={`${fmt(totalSpent, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })} speso`}
            icon={Euro}
            tone="neutral"
          />
          <StatCard
            label="Campagne Attive"
            value={activeCampaigns}
            {...(total > 0 ? { hint: `${Math.round((activeCampaigns / total) * 100)}% del totale` } : {})}
            icon={TrendingUp}
            tone="success"
          />
          <StatCard
            label="CTR Medio"
            value={avgCtr > 0 ? `${avgCtr.toFixed(2)}%` : '—'}
            hint={metricsWithCtr.length > 0 ? `su ${metricsWithCtr.length} metriche` : 'Nessuna metrica disponibile'}
            icon={BarChart3}
            tone="neutral"
          />
        </div>
      )}

      {/* Filters + search + view toggle */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Cerca campagne..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg w-52 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 placeholder:text-slate-400"
            />
          </div>

          {/* Status filter chips — only for statuses present in data */}
          {!isLoading && presentStatuses.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {presentStatuses.map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(filterStatus === s ? '' : s)}
                  className={cn(
                    'transition-opacity',
                    filterStatus && filterStatus !== s ? 'opacity-40' : ''
                  )}
                >
                  <Badge variant={STATUS_VARIANT[s]} dot size="sm">
                    {STATUS_LABEL[s]}
                  </Badge>
                </button>
              ))}
            </div>
          )}

          {/* View toggle */}
          <div className="flex items-center bg-slate-100 rounded-full p-1 ml-auto">
            <button
              onClick={() => setViewMode('grid')}
              className={cn('flex items-center justify-center p-1.5 rounded-full transition-all', viewMode === 'grid' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn('flex items-center justify-center p-1.5 rounded-full transition-all', viewMode === 'list' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Platform + type sub-filters */}
        {!isLoading && (
          <div className="flex items-center gap-3 flex-wrap">
            {presentPlatforms.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {presentPlatforms.map((p) => (
                  <button
                    key={p}
                    onClick={() => setFilterPlatform(filterPlatform === p ? '' : p)}
                    className={cn(
                      'px-2.5 py-1 text-xs font-medium rounded-full border transition-colors',
                      filterPlatform === p
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    )}
                  >
                    {PLATFORM_LABEL[p] ?? p}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-1.5">
              {(['ALWAYS_ON', 'SPOT'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setFilterType(filterType === t ? '' : t)}
                  className={cn(
                    'px-2.5 py-1 text-xs font-medium rounded-full border transition-colors',
                    filterType === t
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  )}
                >
                  {t === 'ALWAYS_ON' ? 'Always On' : 'Spot'}
                </button>
              ))}
            </div>

            {(filterStatus || filterPlatform || filterType || search) && (
              <button
                onClick={() => { setFilterStatus(''); setFilterPlatform(''); setFilterType(''); setSearch('') }}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                Rimuovi filtri
              </button>
            )}
          </div>
        )}
      </div>

      {/* API error */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-2xl">
          <p className="text-sm font-medium text-danger-700 mb-0.5">Errore nel caricamento</p>
          <p className="text-xs text-danger-600">
            Impossibile caricare le campagne. Verifica la connessione al server.
          </p>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <CampaignSkeleton key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-64 bg-white rounded-2xl border border-slate-200 shadow-card">
          <div className="p-4 bg-slate-50 rounded-full mb-4">
            <Megaphone className="w-8 h-8 text-slate-300" />
          </div>
          {campaigns.length === 0 ? (
            <>
              <h3 className="text-sm font-medium text-slate-700 mb-1">Nessuna campagna creata</h3>
              <p className="text-sm text-slate-400 mb-4">Inizia creando la tua prima campagna pubblicitaria</p>
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nuova Campagna
              </button>
            </>
          ) : (
            <>
              <h3 className="text-sm font-medium text-slate-700 mb-1">Nessuna campagna trovata</h3>
              <p className="text-sm text-slate-400">Prova a modificare i filtri di ricerca</p>
            </>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onClick={() => setSelectedCampaign(campaign)}
              onEdit={handleEditCampaign}
              onDeleted={() => {
                // If currently viewing this campaign in detail modal, close it
                if (selectedCampaign?.id === campaign.id) setSelectedCampaign(null)
              }}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden overflow-x-auto">
          <div className="grid grid-cols-[1fr_120px_100px_100px_80px_80px] gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50 min-w-[640px]">
            {['Campagna', 'Piattaforma', 'Stato', 'Budget', 'Tipo', 'Azioni'].map((h) => (
              <span key={h} className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</span>
            ))}
          </div>
          <div className="divide-y divide-slate-200 min-w-[640px]">
            {filtered.map((c) => (
              <div
                key={c.id}
                className="grid grid-cols-[1fr_120px_100px_100px_80px_80px] gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer items-center"
                onClick={() => setSelectedCampaign(c)}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                  <p className="text-xs text-slate-400 truncate">{c.client?.name}</p>
                </div>
                <span className="text-xs text-slate-600">
                  {c.platforms.map(p => PLATFORM_LABEL[p] ?? p).join(', ')}
                </span>
                <Badge variant={STATUS_VARIANT[c.status as CampaignStatus]} dot size="sm">
                  {STATUS_LABEL[c.status as CampaignStatus]}
                </Badge>
                <span className="text-xs text-slate-700 font-medium tabular-nums">
                  {fmt(c.totalBudget, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                </span>
                <span className="text-xs text-slate-500">
                  {c.type === 'ALWAYS_ON' ? 'Always On' : 'Spot'}
                </span>
                {/* Actions in list view */}
                <div
                  className="flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    title="Modifica campagna"
                    onClick={() => handleEditCampaign(c)}
                    className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaign detail modal */}
      {selectedCampaign && !editingCampaign && (
        <CampaignDetailModal
          campaign={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          onEdit={handleEditCampaign}
        />
      )}

      {/* Create form modal */}
      {showCreateForm && (
        <CampaignFormModal
          onClose={handleFormClose}
          onSuccess={handleFormClose}
        />
      )}

      {/* Edit form modal */}
      {editingCampaign && (
        <CampaignFormModal
          campaign={editingCampaign}
          onClose={handleFormClose}
          onSuccess={handleFormClose}
        />
      )}
    </div>
  )
}
